<template>
  <div class="table-wrap" :style="scaleStyle">
    <my-loader v-if="isLoading && !items.length" />
    <div v-else class="table-scroll">
      <table v-if="items.length" class="downtimes-table" :style="{ width: `${table.getTotalSize()}px` }">
        <thead>
          <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
            <th v-for="header in headerGroup.headers" :key="header.id" :style="{ width: `${header.getSize()}px` }">
              <button v-if="header.column.getCanSort()" class="th-sort" @click="sortBy(header.column.id)">
                {{ header.column.columnDef.header }} {{ sortLabel(header.column.id) }}
              </button>
              <span v-else>{{ header.column.columnDef.header }}</span>
              <span class="column-resizer" :class="{ resizing: header.column.getIsResizing() }" @mousedown.stop="header.getResizeHandler()($event)" @touchstart.stop="header.getResizeHandler()($event)" @dblclick.stop="header.column.resetSize()" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in table.getRowModel().rows" :key="row.id" class="data-row" :class="[rowClass(row.original, rowIndex), { stale: row.original.is_tms_stale }]" tabindex="0" @click="$emit('navigate', row.original.cargo_trip_id)" @keydown.enter.self="$emit('navigate', row.original.cargo_trip_id)" @keydown.space.self.prevent="$emit('navigate', row.original.cargo_trip_id)">
            <td v-for="cell in row.getVisibleCells()" :key="cell.id" :style="{ width: `${cell.column.getSize()}px` }">
              <template v-if="cell.column.id === 'downtime_classification'">
                <span class="tag" :style="classificationStyle(row.original.downtime_classification)">{{ classificationMeta(row.original.downtime_classification).shortLabel }}</span>
              </template>
              <template v-else-if="cell.column.id === 'trip_id'">
                <strong>{{ row.original.trip_id || row.original.cargo_trip_id }}</strong>
                <span v-if="row.original.is_tms_stale" class="stale-marker" title="Устаревшие данные TMS">stale</span>
              </template>
              <template v-else-if="cell.column.id === 'warehouse_name'">{{ row.original.warehouse_name || '—' }}</template>
              <template v-else-if="cell.column.id === 'manager_name'">{{ row.original.manager_name || '—' }}</template>
              <template v-else-if="cell.column.id === 'operation_type'">{{ formatOperation(row.original) }}</template>
              <template v-else-if="cell.column.id === 'planned_date'">{{ formatNaiveDate(row.original.planned_date) }}</template>
              <template v-else-if="cell.column.id === 'downtime_start'">{{ formatNaiveTime(row.original.downtime_start) }}</template>
              <template v-else-if="cell.column.id === 'arrival_time'">{{ formatNaiveDate(row.original.arrival_time) }}</template>
              <template v-else-if="cell.column.id === 'departure_time'">{{ formatNaiveDate(row.original.departure_time) }}</template>
              <template v-else-if="cell.column.id === 'delay_minutes'">{{ formatMinutes(row.original.delay_minutes) }}</template>
              <template v-else-if="cell.column.id === 'overdue_hours'">{{ formatHours(row.original.overdue_hours) }}</template>
              <template v-else-if="cell.column.id === 'downtime_cost'">{{ fmtMoney(row.original.downtime_cost) }}</template>
              <template v-else-if="cell.column.id === 'email_delivery'">
                <span class="tag" :style="emailDeliveryStyle(row.original)" :title="emailDeliveryMeta(row.original).title">
                  {{ emailDeliveryMeta(row.original).label }}<template v-if="emailDeliveryMeta(row.original).detail"> {{ emailDeliveryMeta(row.original).detail }}</template>
                </span>
              </template>
              <template v-else-if="cell.column.id === 'registration_source'">{{ formatRegistrationSource(row.original.registration_source) }}</template>
              <template v-else-if="cell.column.id === 'work_status'">
                <span class="tag" :style="workStatusStyle(row.original)">{{ workStatusShortLabel(row.original, workStatusOptions) }}</span>
              </template>
              <template v-else-if="cell.column.id === 'actions'">
                <div class="actions" @click.stop>
                  <button v-if="!row.original.downtime_registered" type="button" class="action-btn register" :disabled="actingId === row.original.route_point_id || !canRegisterDowntime(row.original)" :title="!canRegisterDowntime(row.original) ? 'Регистрировать можно только подтверждённый или потенциальный простой' : ''" @click="$emit('register', row.original)">
                    {{ actingId === row.original.route_point_id ? '…' : 'Регистр.' }}
                  </button>
                  <button v-else type="button" class="action-btn resolve" :disabled="actingId === row.original.route_point_id || !canResolveDowntime(row.original)" @click="$emit('resolve', row.original)">
                    {{ actingId === row.original.route_point_id ? '…' : 'Закрыть' }}
                  </button>
                  <button type="button" class="action-btn docx" :disabled="downloadingActId === row.original.route_point_id || !canDownloadDowntimeAct(row.original)" :title="!canDownloadDowntimeAct(row.original) ? 'Требуется регистрация простоя' : ''" @click="$emit('downloadAct', row.original)">
                    {{ downloadingActId === row.original.route_point_id ? '…' : 'DOCX' }}
                  </button>
                </div>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="isRefreshing && items.length" class="refresh-bar" />
      <div v-if="!items.length && !isLoading" class="empty-table">Записей по заданным фильтрам нет</div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { functionalUpdate, getCoreRowModel, useVueTable } from '@tanstack/vue-table';
import { tableScaleStyle } from '@/components/UI/tableScale.mjs';
import { canDownloadDowntimeAct, canRegisterDowntime, canResolveDowntime } from '../downtimeState.mjs';
import { classificationMeta, emailDeliveryMeta, formatHours, formatMinutes, formatNaiveDate, formatNaiveTime, formatOperation, formatRegistrationSource, getDowntimeHighlightIndexes, getDowntimeRowHighlightClass, workStatusShortLabel } from '../downtimeOptions.mjs';
import { fmtMoney } from '@/pages/statistics/format';

export default {
  name: 'DowntimesTable',
  props: {
    items: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    isRefreshing: { type: Boolean, default: false },
    sorting: { type: Array, default: () => [] },
    columnOrder: { type: Array, default: () => [] },
    columnVisibility: { type: Object, default: () => ({}) },
    columnSizing: { type: Object, default: () => ({}) },
    currentPage: { type: Number, default: 1 },
    pageCount: { type: Number, default: 1 },
    workStatusOptions: { type: Array, default: () => [] },
    actingId: { type: Number, default: null },
    downloadingActId: { type: Number, default: null },
    scale: { type: String, default: 'normal' },
    highlightMode: { type: String, default: 'none' },
    palette: { type: String, default: 'colorful' },
  },
  emits: ['sortingChanged', 'columnSizingChanged', 'navigate', 'register', 'resolve', 'downloadAct'],
  setup(props, { emit }) {
    const columns = [
      { id: 'downtime_classification', header: 'Класс', size: 85, minSize: 78, maxSize: 160, enableSorting: false },
      { id: 'trip_id', header: 'Рейс', size: 95, minSize: 80, maxSize: 170 },
      { id: 'warehouse_name', header: 'Склад', size: 150, minSize: 90, maxSize: 400, enableSorting: false },
      { id: 'manager_name', header: 'Менеджер', size: 150, minSize: 90, maxSize: 240 },
      { id: 'operation_type', header: 'Операция', size: 95, minSize: 82, maxSize: 140, enableSorting: false },
      { id: 'planned_date', header: 'План', size: 138, minSize: 125, maxSize: 180 },
      { id: 'downtime_start', header: 'Порог', size: 62, minSize: 55, maxSize: 110, enableSorting: false },
      { id: 'arrival_time', header: 'Прибытие', size: 138, minSize: 125, maxSize: 180 },
      { id: 'departure_time', header: 'Убытие', size: 138, minSize: 125, maxSize: 180 },
      { id: 'delay_minutes', header: 'Задержка', size: 82, minSize: 70, maxSize: 130 },
      { id: 'overdue_hours', header: 'Простой', size: 75, minSize: 65, maxSize: 120 },
      { id: 'downtime_cost', header: 'Стоимость', size: 105, minSize: 90, maxSize: 150 },
      { id: 'email_delivery', header: 'Email', size: 165, minSize: 100, maxSize: 220, enableSorting: false },
      { id: 'registration_source', header: 'Регистрация', size: 95, minSize: 85, maxSize: 140, enableSorting: false },
      { id: 'work_status', header: 'Статус рейса', size: 150, minSize: 128, maxSize: 240, enableSorting: false },
      { id: 'actions', header: 'Действия', size: 150, minSize: 140, maxSize: 210, enableSorting: false },
    ];
    const scaleStyle = computed(() => tableScaleStyle(props.scale));
    const table = useVueTable({
      get data() { return props.items; },
      columns,
      getCoreRowModel: getCoreRowModel(),
      getRowId: (row, index) => String(row.route_point_id || `${row.cargo_trip_id}-${index}`),
      manualSorting: true,
      manualPagination: true,
      get pageCount() { return props.pageCount; },
      columnResizeMode: 'onChange',
      state: {
        get sorting() { return props.sorting; },
        get columnOrder() { return props.columnOrder; },
        get columnVisibility() { return props.columnVisibility; },
        get columnSizing() { return props.columnSizing; },
        get pagination() { return { pageIndex: Math.max(0, props.currentPage - 1), pageSize: 50 }; },
      },
      onSortingChange: (updater) => emit('sortingChanged', functionalUpdate(updater, props.sorting)),
      onColumnSizingChange: (updater) => emit('columnSizingChanged', functionalUpdate(updater, props.columnSizing)),
    });
    const sortBy = (id) => {
      const current = props.sorting[0];
      emit('sortingChanged', [{ id, desc: current?.id === id ? !current.desc : false }]);
    };
    const sortLabel = (id) => {
      const index = props.sorting.findIndex((item) => item.id === id);
      return index < 0 ? '' : `${props.sorting[index].desc ? '↓' : '↑'}${index + 1}`;
    };
    const classificationStyle = (value) => {
      const meta = classificationMeta(value);
      return { color: meta.color, backgroundColor: meta.background };
    };
    const workStatusStyle = (item) => {
      const option = props.workStatusOptions.find((value) => value.value === Number(item.work_status_id));
      return { color: option?.color || '#475467', backgroundColor: option?.background || '#f2f4f7' };
    };
    const emailDeliveryStyle = (item) => {
      const meta = emailDeliveryMeta(item);
      return { color: meta.color, backgroundColor: meta.background };
    };
    const highlightIndexes = computed(() => getDowntimeHighlightIndexes(props.items, props.highlightMode));
    const rowClass = (item, index) => getDowntimeRowHighlightClass(item, props.highlightMode, props.palette, highlightIndexes.value[index]);
    return {
      table,
      scaleStyle,
      sortBy,
      sortLabel,
      classificationMeta,
      classificationStyle,
      emailDeliveryMeta,
      emailDeliveryStyle,
      workStatusStyle,
      workStatusShortLabel,
      rowClass,
      formatNaiveDate,
      formatNaiveTime,
      formatOperation,
      formatRegistrationSource,
      formatHours,
      formatMinutes,
      fmtMoney,
      canRegisterDowntime,
      canResolveDowntime,
      canDownloadDowntimeAct,
    };
  },
};
</script>

<style scoped>
.table-wrap { position: relative; min-height: 160px; color: #333; font-size: 13px; }
.table-scroll { position: relative; overflow-x: auto; }
.downtimes-table { width: 100%; min-width: 100%; border-collapse: collapse; table-layout: fixed; }
.downtimes-table th, .downtimes-table td { box-sizing: border-box; height: var(--table-cell-height); padding: var(--table-cell-padding-y) var(--table-cell-padding-x); overflow: hidden; border-bottom: 1px solid #edf0f3; font-size: var(--table-cell-font-size); line-height: var(--table-cell-line-height); text-align: left; text-overflow: ellipsis; vertical-align: middle; white-space: nowrap; }
.downtimes-table th { position: relative; background: #f7f8fa; color: #444; font-weight: 700; }
.th-sort { padding: 0; border: 0; background: transparent; color: inherit; font: inherit; cursor: pointer; }
.th-sort:hover { color: #007bff; }
.column-resizer { position: absolute; z-index: 2; top: 0; right: 0; width: 10px; height: 100%; cursor: col-resize; touch-action: none; }
.column-resizer::before { position: absolute; top: 4px; right: 4px; bottom: 4px; width: 1px; background: #aeb8c5; content: ''; }
.column-resizer:hover::before, .column-resizer.resizing::before { background: #007bff; }
.data-row { cursor: pointer; }
.data-row:hover { background: #f3f6fb; }
.data-row[class*="hl-"]:hover { filter: brightness(0.97); }
.data-row.stale { color: #7d8898; opacity: .7; }
.data-row.hl-0 { background: #eef6ff; } .data-row.hl-1 { background: #fef3f2; }
.data-row.hl-2 { background: #ecfdf3; } .data-row.hl-3 { background: #fffaeb; }
.data-row.hl-4 { background: #f4f3ff; } .data-row.hl-5 { background: #fff1f3; }
.data-row.hl-6 { background: #f0fdf4; } .data-row.hl-7 { background: #eff8ff; }
.data-row.hl-8 { background: #fefce8; } .data-row.hl-9 { background: #fdf2f8; }
.data-row.hl-10 { background: #f0f9ff; } .data-row.hl-11 { background: #f5f3ff; }
.data-row.hl-zebra-0 { background: #f8fafc; } .data-row.hl-zebra-1 { background: #fff; }
.data-row.hl-bee-0 { background: #fef9c3; } .data-row.hl-bee-1 { background: #fff; }
.data-row.hl-cls-confirmed { background: #fee4e2; }
.data-row.hl-cls-late { background: #ffead5; }
.data-row.hl-cls-potential { background: #fef0c7; }
.data-row.hl-cls-incomplete { background: #eff8ff; }
.data-row.hl-cls-upcoming { background: #f0fdfa; }
.data-row.hl-cls-resolved { background: #ecfdf3; }
.data-row.hl-cls-none { background: #ffffff; }
.data-row.hl-ws-1 { background: #eff8ff; } .data-row.hl-ws-2 { background: #ecfdf3; }
.data-row.hl-ws-3 { background: #fffaeb; } .data-row.hl-ws-9 { background: #f4f3ff; }
.data-row.hl-ws-10 { background: #fff1f3; } .data-row.hl-ws-13 { background: #f2f4f7; }
.tag { display: inline-block; max-width: 100%; padding: 2px 7px; overflow: hidden; border-radius: 10px; font-size: 11px; font-weight: 700; text-overflow: ellipsis; }
.stale-marker { margin-left: 4px; padding: 1px 5px; border-radius: 4px; background: #eaecf0; color: #667085; font-size: 10px; }
.actions { display: flex; gap: 4px; }
.action-btn { height: 24px; padding: 0 6px; border: 1px solid #d0d5dd; border-radius: 4px; background: #fff; font-size: 11px; font-weight: 700; cursor: pointer; }
.action-btn.register { border-color: #007bff; color: #007bff; }
.action-btn.resolve { border-color: #039855; color: #039855; }
.action-btn.docx { color: #475467; }
.action-btn:disabled { cursor: not-allowed; opacity: .4; }
.empty-table { padding: 28px; color: #999; font-size: 14px; }
.refresh-bar { position: absolute; z-index: 5; top: 0; left: 0; width: 100%; height: 2px; overflow: hidden; }
.refresh-bar::after { display: block; width: 35%; height: 100%; background: #007bff; animation: refresh-slide 1s linear infinite; content: ''; }
@keyframes refresh-slide { from { transform: translateX(-100%); } to { transform: translateX(300%); } }
</style>
